import { env } from '../env';
import { initZoom, zoomSdk } from './zoom';

// Transient (per-tab) handshake state — cleared as soon as the OAuth roundtrip
// completes, so sessionStorage is the right scope for these.
const STATE_KEY = 'lh.oauth.state';
const VERIFIER_KEY = 'lh.oauth.verifier';

// Long-lived session — uses localStorage so the mediator stays signed in
// across Zoom restarts. XSS risk is mitigated by a tight script-src CSP.
const SESSION_KEY = 'lh.zoom.session';

export interface ZoomSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  scope: string;
  userId: string;
  displayName?: string;
}

const base64UrlEncode = (bytes: Uint8Array): string => {
  let str = '';
  for (const byte of bytes) str += String.fromCharCode(byte);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const randomBytes = (length: number): Uint8Array => {
  const buf = new Uint8Array(length);
  crypto.getRandomValues(buf);
  return buf;
};

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(digest);
}

/**
 * Kick off the Zoom OAuth authorization code + PKCE flow. When the app runs
 * inside Zoom we call `zoomSdk.authorize()` so the handshake happens inside
 * the client; outside Zoom (e.g. local browser testing) we fall back to a
 * full-page redirect.
 */
export async function beginAuthorize(): Promise<void> {
  const state = base64UrlEncode(randomBytes(16));
  const verifier = base64UrlEncode(randomBytes(32));

  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  try {
    await initZoom();
    // Zoom Apps SDK's `authorize` accepts only `codeChallenge` (no method
    // selector). Empirically Zoom's In-Client OAuth treats the challenge as
    // `plain` — i.e. it expects the token-exchange `code_verifier` to equal
    // the `codeChallenge` we passed here. Sending an S256 hash leads to
    // `invalid_grant`. So: challenge = verifier.
    await zoomSdk.authorize({
      state,
      codeChallenge: verifier,
    });
    return;
  } catch {
    // Not running inside Zoom — fall back to a standard redirect (which
    // does support S256).
  }

  const challenge = base64UrlEncode(await sha256(verifier));

  const url = new URL('https://zoom.us/oauth/authorize');
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', env.zoomClientId);
  url.searchParams.set('redirect_uri', env.zoomRedirectUri);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(url.toString());
}

interface TokenExchangeResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  scope: string;
  user: { id: string; display_name?: string };
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  scope: string;
}

/**
 * Completes OAuth by POSTing the code + PKCE verifier to our Pages Function,
 * which does the confidential client exchange with Zoom.
 *
 * `state` is optional because Zoom's In-Client OAuth does not always echo it
 * back in the `onAuthorized` event. When state is provided we still validate
 * it for CSRF protection; when it isn't, the PKCE verifier serves as the
 * binding between our authorize request and this exchange.
 */
export async function completeAuthorize(params: {
  code: string;
  state: string | null;
  /**
   * The redirect URI Zoom actually used for this authorize flow. In the
   * in-client OAuth path, Zoom ships this on the `onAuthorized` event and it
   * can differ from what's registered in the Marketplace app settings — the
   * token endpoint rejects the exchange unless we echo this exact value
   * back.
   */
  redirectUri?: string | null;
}): Promise<ZoomSession> {
  // Atomic consume: read + remove in one beat so a racing caller sees a
  // missing verifier and bails out instead of sending a stale one.
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  console.log('[auth] completeAuthorize', {
    codePrefix: params.code.slice(0, 6),
    stateFromEvent: params.state?.slice(0, 6),
    stateFromStorage: expectedState?.slice(0, 6),
    hasVerifier: Boolean(verifier),
    verifierPrefix: verifier?.slice(0, 6),
    redirectUri: params.redirectUri,
  });
  if (params.state && expectedState && expectedState !== params.state) {
    throw new Error('OAuth state mismatch — possible CSRF');
  }
  if (!verifier) {
    throw new Error('Missing PKCE verifier — start the sign-in flow again');
  }

  const res = await fetch('/api/oauth/callback', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      code: params.code,
      code_verifier: verifier,
      redirect_uri: params.redirectUri ?? undefined,
    }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Token exchange failed (${res.status})`);
  }

  const data = (await res.json()) as TokenExchangeResponse;
  console.log('[auth] token granted', { scopes: data.scope.split(' ') });
  const session: ZoomSession = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
    scope: data.scope,
    userId: data.user.id,
    displayName: data.user.display_name,
  };

  saveSession(session);
  return session;
}

/**
 * Thrown when the refresh token is rejected by Zoom (invalid/expired/revoked).
 * Callers should clear the session and drop to the sign-in prompt.
 */
export class RefreshTokenInvalidError extends Error {
  constructor() {
    super('Zoom refresh token is no longer valid');
  }
}

/**
 * Swap a refresh token for a fresh access token. Zoom rotates refresh tokens
 * on every use, so the response carries a new one that we persist in place
 * of the old. Concurrent callers share a single in-flight request so we
 * don't burn refresh tokens racing each other.
 */
let refreshInFlight: Promise<ZoomSession> | null = null;

export function refreshSession(current: ZoomSession): Promise<ZoomSession> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const res = await fetch('/api/oauth/refresh', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: current.refreshToken }),
    });

    if (res.status === 401) {
      clearSession();
      throw new RefreshTokenInvalidError();
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `Refresh failed (${res.status})`);
    }

    const data = (await res.json()) as RefreshResponse;
    const next: ZoomSession = {
      ...current,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: Date.now() + data.expires_in * 1000,
      scope: data.scope,
    };
    saveSession(next);
    return next;
  })();

  refreshInFlight.finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

function saveSession(session: ZoomSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

/**
 * Returns the stored session if any, without regard for whether the access
 * token has expired — callers inspect `expiresAt` and decide whether to
 * refresh. Returns null only when nothing is stored or the record is corrupt.
 */
export function loadSession(): ZoomSession | null {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ZoomSession;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}
